import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion, AnimatePresence } from "framer-motion";

/**
 * Comprime una imagen usando la Canvas API del navegador.
 * @param {File} file - Archivo de imagen original
 * @param {number} maxDimension - Dimensión máxima (ancho o alto) en px. Default: 1920
 * @param {number} quality - Calidad JPEG de 0 a 1. Default: 0.82
 * @returns {Promise<File>} - Archivo comprimido
 */
const compressImage = (file, maxDimension = 1920, quality = 0.82) => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;

      // Calcular nuevas dimensiones manteniendo proporción
      if (width > maxDimension || height > maxDimension) {
        if (width >= height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            // Si falla la compresión, devolver el archivo original
            resolve(file);
            return;
          }
          const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file); // Si falla, usar original
    };

    img.src = objectUrl;
  });
};

const COMPRESSIBLE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

export default function FormFileUpload({ field, error, onChange, value }) {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef(null);

  // Get accepted file types from field config
  const acceptedTypes = field.acceptedTypes || [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "application/pdf",
  ];
  const maxSize = field.maxSize || 5; // Default 5MB
  const icon = field.icon || "fa-solid fa-cloud-arrow-up";
  const showImage = field.showImage;
  const showStyle = field.showStyle;

  // Sincronizar estado interno con el valor del formData
  useEffect(() => {
    if (value && value instanceof File) {
      // Si hay un valor (archivo) almacenado, reconstruir el estado interno
      setUploadedFile({
        name: value.name,
        size: value.size,
        type: value.type,
      });

      // Crear preview para archivos de imagen
      if (value.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(value);
      } else {
        // Para archivos no-imagen (como PDF), limpiar preview de imagen
        setPreview(null);
      }
    } else if (!value) {
      // Si no hay valor, limpiar estados
      setPreview(null);
      setUploadedFile(null);
      setUploadError(null);
    }
  }, [value]);

  // Format accepted types for display
  const getAcceptedTypesDisplay = () => {
    return acceptedTypes
      .map((type) => type.split("/")[1].toUpperCase())
      .join(", ");
  };


  // Handle file selection
  const handleFileChange = async (file) => {
    if (!file) return;

    setUploadError(null);

    // Validate file type first (before compression)
    if (!acceptedTypes.includes(file.type)) {
      setUploadError(`Tipo de archivo no permitido. Formatos aceptados: ${getAcceptedTypesDisplay()}`);
      return;
    }

    // Compress image if applicable
    const shouldCompress = field.compress !== false && COMPRESSIBLE_TYPES.includes(file.type);
    let processedFile = file;

    if (shouldCompress) {
      setIsCompressing(true);
      try {
        processedFile = await compressImage(
          file,
          field.maxDimension || 1920,
          field.compressQuality || 0.82
        );
      } finally {
        setIsCompressing(false);
      }
    }

    // Validate size after compression
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (processedFile.size > maxSizeBytes) {
      setUploadError(`El archivo debe ser menor a ${maxSize}MB`);
      return;
    }

    // Store file information
    setUploadedFile({
      name: processedFile.name,
      size: processedFile.size,
      type: processedFile.type,
    });

    // Create preview for image files
    if (processedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(processedFile);
    } else {
      // For non-image files (like PDF), clear image preview
      setPreview(null);
    }

    // Pass file to parent component
    onChange(field.name, processedFile);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileChange(file);
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    handleFileChange(file);
  };

  // Handle click
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Clear file
  const handleClear = (e) => {
    e.stopPropagation();
    setPreview(null);
    setUploadedFile(null);
    setUploadError(null);
    onChange(field.name, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const displayError = uploadError || error;

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-200 mb-2">
        {field.label}
        {field.required && (
          <span className="text-aw-green font-semibold text-lg ml-1">*</span>
        )}
        {field.example && (
          <a
            href={field.example}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs inline text-aw-green ml-2"
          >
            ( Ver Ejemplo )
          </a>
        )}
        {field.infoUrl && (
          <a
            href={field.infoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs inline-block underline text-aw-green ml-2"
          >
            {field.infoUrlLabel}
          </a>
        )}
      </label>

      <div
        onClick={!isCompressing ? handleClick : undefined}
        onDragEnter={!isCompressing ? handleDrag : undefined}
        onDragOver={!isCompressing ? handleDrag : undefined}
        onDragLeave={!isCompressing ? handleDrag : undefined}
        onDrop={!isCompressing ? handleDrop : undefined}
        className={`group/uploadinput relative w-full aspect-square max-w-xs mx-auto max-h-44 rounded-lg border-2 transition-all duration-300 overflow-hidden ${
          isCompressing
            ? "border-aw-green cursor-wait"
            : dragActive
            ? "border-aw-green bg-aw-green/10 scale-105 cursor-pointer"
            : displayError
            ? "border-red-500 border-dashed cursor-pointer"
            : preview
            ? "border-aw-green cursor-pointer"
            : "border-gray-300 border-dashed hover:border-aw-green cursor-pointer"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(",")}
          onChange={handleInputChange}
          className="hidden"
        />

        <AnimatePresence mode="wait">
          {isCompressing ? (
            <Motion.div
              key="compressing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-aw-bluedark/80"
            >
              <FontAwesomeIcon
                icon="fa-solid fa-spinner"
                className="text-aw-green text-4xl animate-spin"
              />
              <p className="text-aw-green text-sm font-medium">Optimizando imagen...</p>
            </Motion.div>
          ) : preview ? (
            // Show image preview
            <Motion.div
              key="preview"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
              className="relative w-full h-full group"
            >
              <img
                src={preview}
                alt="Preview"
                className={` ${showImage} object-cover`}
              />
              {/* Overlay on hover */}
              <div
                className={`absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-2 ${showStyle}`}
              >
                <FontAwesomeIcon
                  icon="fa-solid fa-arrows-rotate"
                  className="text-white text-3xl"
                />
                <span className="text-white font-medium">Cambiar imagen</span>
              </div>
              {/* Clear button */}
              <button
                onClick={handleClear}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-colors duration-200 opacity-0 group-hover:opacity-100"
              >
                <FontAwesomeIcon icon="fa-solid fa-xmark" />
              </button>
            </Motion.div>
          ) : uploadedFile ? (
            // Show PDF/file preview
            <Motion.div
              key="file-preview"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
              className="relative w-full h-full group bg-gradient-to-br from-aw-green/10 to-aw-bluedark/10"
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
                {/* Success icon */}
                <div className="bg-aw-green rounded-full p-4">
                  <FontAwesomeIcon
                    icon={
                      uploadedFile.type === "application/pdf"
                        ? "fa-solid fa-file-pdf"
                        : "fa-solid fa-file"
                    }
                    className="text-white text-4xl"
                  />
                </div>
                {/* File info */}
                <div className="relative text-center">
                  <p className="text-aw-green font-bold mb-1 flex items-center gap-2 justify-center">
                    <FontAwesomeIcon icon="fa-solid fa-circle-check" />
                    Archivo aceptado
                  </p>
                  <p className="text-gray-300 text-sm font-medium truncate max-w-full px-2 line-clamp-1 w-64 mx-auto">
                    {uploadedFile.name}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {formatFileSize(uploadedFile.size)}
                  </p>
                </div>
              </div>
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-2">
                <FontAwesomeIcon
                  icon="fa-solid fa-arrows-rotate"
                  className="text-white text-3xl"
                />
                <span className="text-white font-medium">Cambiar archivo</span>
              </div>
              {/* Clear button */}
              <button
                onClick={handleClear}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-colors duration-200 opacity-0 group-hover:opacity-100"
              >
                <FontAwesomeIcon icon="fa-solid fa-xmark" />
              </button>
            </Motion.div>
          ) : (
            // Show upload prompt
            <Motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center"
            >
              <FontAwesomeIcon
                icon={icon}
                className={`group-hover/uploadinput:text-aw-green text-6xl transition-colors duration-300 ${
                  dragActive ? "text-aw-green" : "text-gray-400"
                }`}
              />
              <div>
                <p className="text-gray-300 font-medium mb-1">
                  {dragActive
                    ? "Suelta el archivo aquí"
                    : "Haz clic o arrastra un archivo"}
                </p>
                <p className="text-xs text-aw-green">
                  {getAcceptedTypesDisplay()} (máx. {maxSize}MB)
                </p>
              </div>
            </Motion.div>
          )}
        </AnimatePresence>
      </div>

      {displayError && (
        <Motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs text-red-500 mt-2 text-center"
        >
          {displayError}
        </Motion.p>
      )}
      {field.helpText && !displayError && (
        <p className="text-xs text-gray-400 mt-2 text-center">
          {field.helpText}
        </p>
      )}
    </div>
  );
}
